import { useState, useEffect, useCallback } from "react";
import { LightPageLayout } from "../components/Layout";
import InstructorLibraryPanel from "../components/InstructorLibraryPanel";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Users, CheckCircle, XCircle, Clock, Eye, UserCheck, UserX,
  ListFilter, ArrowRightLeft, FileText, Phone, Mail, GraduationCap,
  CalendarDays, ChevronDown, ChevronUp, Shield, Send, UserPlus
} from "lucide-react";

const STATUS_BADGES = {
  submitted: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200" },
  under_review: { label: "Reviewing", color: "bg-blue-100 text-blue-800 border-blue-200" },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200" },
  waitlisted: { label: "Waitlisted", color: "bg-purple-100 text-purple-800 border-purple-200" },
  transferred: { label: "Transferred", color: "bg-slate-100 text-slate-600 border-slate-200" },
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d + (d.includes("T") ? "" : "T00:00:00")).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  });
};

// ============================================
// STUDENT DETAIL MODAL
// ============================================
const StudentDetailModal = ({ enrollment, open, onClose, onAction, allPrograms }) => {
  const [notes, setNotes] = useState("");
  const [transferTarget, setTransferTarget] = useState({ program_id: "", cohort_id: "" });
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAssignCohort, setShowAssignCohort] = useState(false);
  const [assignCohortId, setAssignCohortId] = useState("");
  const [acting, setActing] = useState(false);

  if (!enrollment) return null;

  const intake = enrollment.intake || {};
  const eligibility = enrollment.eligibility || {};
  const programApp = enrollment.program_application || {};
  const ps = enrollment.program_selection || {};

  const handleAction = async (action) => {
    setActing(true);
    const payload = { action, notes };
    if (action === "transfer") {
      if (!transferTarget.program_id || !transferTarget.cohort_id) {
        toast.error("Select a target program and cohort for transfer");
        setActing(false);
        return;
      }
      payload.target_program_id = transferTarget.program_id;
      payload.target_cohort_id = transferTarget.cohort_id;
    }
    await onAction(enrollment.enrollment_id, payload);
    setActing(false);
    setNotes("");
    setShowTransfer(false);
  };

  const handleAssignToCohort = async () => {
    if (!assignCohortId) {
      toast.error("Select a cohort");
      return;
    }
    setActing(true);
    try {
      const res = await fetch(`${API}/enrollment/${enrollment.enrollment_id}/assign-cohort`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cohort_id: assignCohortId, notes }),
      });
      if (res.ok) {
        toast.success("Enrollee assigned to cohort!");
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Assignment failed");
      }
    } catch { toast.error("Assignment failed"); }
    finally { setActing(false); }
  };

  const targetProgram = allPrograms.find(p => p.program_id === transferTarget.program_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 bg-white">
        <DialogHeader className="bg-gradient-to-r from-[#1E3A8A] to-[#152C6E] p-5 text-white rounded-t-lg">
          <DialogTitle className="text-white flex items-center gap-3">
            {enrollment.user_picture ? (
              <img src={enrollment.user_picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                {(enrollment.applicant_name || "?")[0]}
              </div>
            )}
            <div>
              <p className="text-lg font-bold">{enrollment.applicant_name || enrollment.user_name}</p>
              <p className="text-white/70 text-sm font-normal">{ps.program_name} &middot; {formatDate(ps.cohort_start_date || ps.start_date)}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] px-6 py-4">
          {/* Contact Info */}
          <Section title="Contact Information">
            <InfoRow icon={Mail} label="Email" value={enrollment.user_email} />
            <InfoRow icon={Phone} label="Phone" value={enrollment.applicant_phone || intake.phone || "Not provided"} />
            <InfoRow icon={null} label="Address" value={[intake.street_address, intake.city, intake.state, intake.zip_code].filter(Boolean).join(", ") || "Not provided"} />
            <InfoRow icon={null} label="Emergency Contact" value={intake.emergency_contact_name ? `${intake.emergency_contact_name} (${intake.emergency_contact_relation}) - ${intake.emergency_contact_phone}` : "Not provided"} />
          </Section>

          {/* Eligibility Summary */}
          <Section title="Eligibility Summary">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <InfoRow label="Citizenship" value={eligibility.citizenship_status || "N/A"} />
              <InfoRow label="Employment" value={eligibility.employment_status || "N/A"} />
              <InfoRow label="Education" value={eligibility.highest_education || "N/A"} />
              <InfoRow label="Veteran" value={eligibility.is_veteran ? "Yes" : "No"} />
            </div>
          </Section>

          {/* Program Application Answers */}
          <Section title="Application Answers">
            {Object.keys(programApp).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No application answers submitted.</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(programApp).filter(([k]) => !["program_id", "program_name", "submitted_at"].includes(k)).map(([key, value]) => (
                  <div key={key} className="border-b border-slate-100 pb-2">
                    <p className="text-xs text-slate-500 font-medium capitalize">{key.replace(/_/g, " ")}</p>
                    <p className="text-sm text-slate-800 mt-0.5">
                      {Array.isArray(value) ? value.join(", ") : (typeof value === "object" && value !== null ? JSON.stringify(value) : (value || "—"))}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Documents */}
          <Section title="Uploaded Documents">
            {(enrollment.documents || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No documents uploaded.</p>
            ) : (
              <div className="space-y-2">
                {enrollment.documents.map((doc, i) => (
                  <a key={i} href={doc.file_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-[#1E3A8A] hover:underline">
                    <FileText className="w-4 h-4" />
                    {doc.document_type || doc.file_name || "Document"}
                    {doc.document_type === "resume" && <Badge className="bg-brand-gold/10 text-brand-navy text-xs border-0">Resume</Badge>}
                  </a>
                ))}
              </div>
            )}
          </Section>

          {/* Review Notes */}
          {enrollment.review_notes && (
            <Section title="Previous Review Notes">
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{enrollment.review_notes}</p>
              <p className="text-xs text-slate-400 mt-1">By {enrollment.reviewer_name || "Admin"} on {formatDate(enrollment.reviewed_at)}</p>
            </Section>
          )}
        </ScrollArea>

        {/* Action Bar */}
        {!["approved", "rejected", "transferred"].includes(enrollment.status) && (
          <div className="border-t border-slate-200 p-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-sm">Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add review notes..."
                className="input-base min-h-[60px]"
                data-testid="review-notes"
              />
            </div>

            {/* Assign to Cohort (for waitlisted students) */}
            {showAssignCohort && (
              <div className="bg-emerald-50 rounded-lg p-3 space-y-2">
                <Label className="text-slate-600 text-sm font-semibold">Assign to Cohort:</Label>
                <div className="flex items-center gap-2">
                  <Select value={assignCohortId} onValueChange={setAssignCohortId}>
                    <SelectTrigger className="flex-1 input-base" data-testid="assign-cohort-select">
                      <SelectValue placeholder="Select cohort..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {(() => {
                        const programId = ps.program_id || (enrollment.program_application || {}).program_id;
                        const program = allPrograms.find(p => p.program_id === programId);
                        const openCohorts = (program?.cohorts || []).filter(c => !c.max_participants || c.enrolled_count < c.max_participants);
                        return openCohorts.length > 0 ? openCohorts.map(c => (
                          <SelectItem key={c.cohort_id} value={c.cohort_id}>
                            {formatDate(c.start_date)} ({(c.max_participants || 0) - (c.enrolled_count || 0)} spots)
                          </SelectItem>
                        )) : (
                          <SelectItem value="none" disabled>All cohorts full</SelectItem>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAssignToCohort} disabled={acting || !assignCohortId}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5"
                    data-testid="confirm-assign-cohort">
                    <CheckCircle className="w-4 h-4" /> Assign
                  </Button>
                </div>
              </div>
            )}

            {showTransfer && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <Label className="text-slate-600 text-sm font-semibold">Transfer to:</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={transferTarget.program_id} onValueChange={(v) => setTransferTarget({ program_id: v, cohort_id: "" })}>
                    <SelectTrigger className="input-base" data-testid="transfer-program"><SelectValue placeholder="Program..." /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {allPrograms.filter(p => p.cohorts.length > 0).map(p => (
                        <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={transferTarget.cohort_id} onValueChange={(v) => setTransferTarget(prev => ({ ...prev, cohort_id: v }))}>
                    <SelectTrigger className="input-base" data-testid="transfer-cohort"><SelectValue placeholder="Cohort..." /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {(targetProgram?.cohorts || []).map(c => (
                        <SelectItem key={c.cohort_id} value={c.cohort_id}>{formatDate(c.start_date)} ({c.max_participants - c.enrolled_count} spots)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => handleAction("approve")} disabled={acting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5"
                data-testid="action-approve">
                <CheckCircle className="w-4 h-4" /> Approve
              </Button>
              <Button onClick={() => handleAction("reject")} disabled={acting}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5"
                data-testid="action-reject">
                <XCircle className="w-4 h-4" /> Reject
              </Button>
              <Button onClick={() => handleAction("waitlist")} disabled={acting}
                className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5"
                data-testid="action-waitlist">
                <Clock className="w-4 h-4" /> Waitlist
              </Button>
              {(enrollment.status === "waitlisted" || enrollment.status === "submitted") && (
                <Button onClick={() => { setShowAssignCohort(!showAssignCohort); setShowTransfer(false); }} variant="outline"
                  className={`flex items-center gap-1.5 ${showAssignCohort ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "btn-outline"}`}
                  data-testid="action-assign-cohort-toggle">
                  <UserPlus className="w-4 h-4" /> Assign to Cohort
                </Button>
              )}
              <Button onClick={() => { setShowTransfer(!showTransfer); setShowAssignCohort(false); }} variant="outline"
                className="btn-outline flex items-center gap-1.5"
                data-testid="action-transfer-toggle">
                <ArrowRightLeft className="w-4 h-4" /> Transfer
              </Button>
              {showTransfer && (
                <Button onClick={() => handleAction("transfer")} disabled={acting || !transferTarget.cohort_id}
                  className="bg-[#1E3A8A] hover:bg-[#152C6E] text-white flex items-center gap-1.5"
                  data-testid="action-transfer-confirm">
                  <Send className="w-4 h-4" /> Confirm Transfer
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm font-semibold text-[#0F1B3D] mb-2 border-b border-slate-100 pb-1">{title}</h3>
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, label, value }) => {
  const displayValue = Array.isArray(value) ? value.join(", ") : (typeof value === "object" && value !== null ? JSON.stringify(value) : value);
  return (
    <div className="flex items-start gap-2 py-1 text-sm">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />}
      <span className="text-slate-500 min-w-[80px]">{label}:</span>
      <span className="text-slate-800">{displayValue}</span>
    </div>
  );
};

// ============================================
// ENROLLMENT ROW
// ============================================
const EnrollmentRow = ({ enrollment, onView }) => {
  const badge = STATUS_BADGES[enrollment.status] || { label: enrollment.status, color: "bg-slate-100 text-slate-600" };
  return (
    <div
      className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg hover:border-brand-gold/40 cursor-pointer transition-colors bg-white"
      onClick={() => onView(enrollment)}
      data-testid={`enrollment-row-${enrollment.enrollment_id}`}
    >
      {enrollment.user_picture ? (
        <img src={enrollment.user_picture} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-[#1E3A8A] flex items-center justify-center text-xs text-white font-bold flex-shrink-0">
          {(enrollment.applicant_name || "?")[0]}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{enrollment.applicant_name || enrollment.user_name}</p>
        <p className="text-xs text-slate-500 truncate">{enrollment.user_email}</p>
      </div>
      <div className="text-xs text-slate-400 hidden sm:block">
        {formatDate(enrollment.created_at)}
      </div>
      <Badge className={`text-xs border ${badge.color}`} data-testid={`status-badge-${enrollment.enrollment_id}`}>
        {badge.label}
      </Badge>
      <Eye className="w-4 h-4 text-slate-300" />
    </div>
  );
};

// ============================================
// COHORT CARD
// ============================================
const CohortCard = ({ cohort, onSelect, isSelected }) => {
  const counts = cohort.status_counts || {};
  const pending = (counts.submitted || 0) + (counts.under_review || 0);
  const approved = counts.approved || 0;
  const waitlisted = counts.waitlisted || 0;

  return (
    <button
      onClick={() => onSelect(cohort)}
      className={`text-left w-full p-4 rounded-xl border-2 transition-all ${
        isSelected ? "border-brand-gold bg-brand-gold/5" : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
      data-testid={`cohort-card-${cohort.cohort_id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">{cohort.program_name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            <CalendarDays className="w-3 h-3 inline mr-1" />
            {formatDate(cohort.start_date)}
          </p>
          {cohort.instructors?.length > 0 && (
            <p className="text-xs text-slate-400 mt-0.5">Instructors: {cohort.instructors.join(", ")}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">{cohort.enrolled_count || 0} / {cohort.max_participants || "∞"}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        {pending > 0 && <MiniStat label="Pending" count={pending} color="text-amber-600 bg-amber-50" />}
        {approved > 0 && <MiniStat label="Approved" count={approved} color="text-emerald-600 bg-emerald-50" />}
        {waitlisted > 0 && <MiniStat label="Waitlisted" count={waitlisted} color="text-purple-600 bg-purple-50" />}
        {!pending && !approved && !waitlisted && <span className="text-xs text-slate-400">No enrollments yet</span>}
      </div>
    </button>
  );
};

const MiniStat = ({ label, count, color }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
    {count} {label}
  </span>
);

// ============================================
// MAIN INSTRUCTOR DASHBOARD
// ============================================
const InstructorDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [allPrograms, setAllPrograms] = useState([]);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/instructor/dashboard`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        if (data.cohorts.length > 0 && !selectedCohort) {
          setSelectedCohort(data.cohorts[0]);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadPrograms = useCallback(async () => {
    try {
      const res = await fetch(`${API}/instructor/programs-cohorts`, { credentials: "include" });
      if (res.ok) setAllPrograms(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  const loadEnrollments = useCallback(async (cohort, statusFilter) => {
    if (!cohort) return;
    setEnrollmentsLoading(true);
    try {
      let url = `${API}/instructor/cohort/${cohort.program_id}/${cohort.cohort_id}/enrollments`;
      if (statusFilter && statusFilter !== "all") url += `?status_filter=${statusFilter}`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setEnrollments(await res.json());
    } catch (err) { console.error(err); }
    finally { setEnrollmentsLoading(false); }
  }, []);

  useEffect(() => { loadDashboard(); loadPrograms(); }, [loadDashboard, loadPrograms]);

  useEffect(() => {
    if (selectedCohort) {
      const statusMap = { pending: "submitted", roster: "approved", waitlist: "waitlisted", all: undefined };
      loadEnrollments(selectedCohort, statusMap[activeTab]);
    }
  }, [selectedCohort, activeTab, loadEnrollments]);

  const viewEnrollmentDetail = async (enrollment) => {
    try {
      const res = await fetch(`${API}/instructor/enrollment/${enrollment.enrollment_id}`, { credentials: "include" });
      if (res.ok) {
        setSelectedEnrollment(await res.json());
        setDetailOpen(true);
      }
    } catch (err) { toast.error("Failed to load student details"); }
  };

  const handleReviewAction = async (enrollmentId, payload) => {
    try {
      const res = await fetch(`${API}/instructor/enrollment/${enrollmentId}/review`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        setDetailOpen(false);
        setSelectedEnrollment(null);
        // Refresh data
        loadDashboard();
        if (selectedCohort) {
          const statusMap = { pending: "submitted", roster: "approved", waitlist: "waitlisted", all: undefined };
          loadEnrollments(selectedCohort, statusMap[activeTab]);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Action failed");
      }
    } catch { toast.error("An error occurred"); }
  };

  if (loading) {
    return (
      <LightPageLayout title="My Classes">
        <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
      </LightPageLayout>
    );
  }

  const cohorts = dashboard?.cohorts || [];
  const pendingEnrollments = enrollments.filter(e => e.status === "submitted" || e.status === "under_review");
  const approvedEnrollments = enrollments.filter(e => e.status === "approved");
  const waitlistedEnrollments = enrollments.filter(e => e.status === "waitlisted");

  return (
    <LightPageLayout
      title="Instructor Dashboard"
      subtitle={`Manage your classes and student enrollments${dashboard?.is_admin ? " (Admin view — all cohorts)" : ""}`}
    >
      <div className="max-w-6xl mx-auto space-y-6" data-testid="instructor-dashboard">
        {cohorts.length === 0 ? (
          <Card className="card-light">
            <CardContent className="py-12 text-center">
              <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Classes Assigned</h3>
              <p className="text-sm text-slate-500 mt-1">
                {dashboard?.is_admin
                  ? "No cohorts with instructor assignments found. Assign instructors to cohorts in the Admin panel."
                  : "You haven't been assigned to any cohorts yet. Contact an admin to get started."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-[320px_1fr] gap-6">
            {/* Cohort Sidebar */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Your Cohorts</h2>
              {cohorts.map((c) => (
                <CohortCard
                  key={`${c.program_id}-${c.cohort_id}`}
                  cohort={c}
                  onSelect={setSelectedCohort}
                  isSelected={selectedCohort?.cohort_id === c.cohort_id && selectedCohort?.program_id === c.program_id}
                />
              ))}
            </div>

            {/* Main Content */}
            {selectedCohort && (
              <div className="space-y-4">
                <Card className="card-light">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-[#0F1B3D] flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-[#1E3A8A]" />
                      {selectedCohort.program_name}
                      <span className="text-sm font-normal text-slate-500 ml-2">
                        Starting {formatDate(selectedCohort.start_date)}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                      <TabsList className="bg-slate-100 mb-4">
                        <TabsTrigger value="pending" className="data-[state=active]:bg-white" data-testid="tab-pending">
                          Pending {pendingEnrollments.length > 0 && `(${enrollments.filter(e => e.status === "submitted" || e.status === "under_review").length})`}
                        </TabsTrigger>
                        <TabsTrigger value="roster" className="data-[state=active]:bg-white" data-testid="tab-roster">
                          Class Roster
                        </TabsTrigger>
                        <TabsTrigger value="waitlist" className="data-[state=active]:bg-white" data-testid="tab-waitlist">
                          Waitlisted
                        </TabsTrigger>
                        <TabsTrigger value="all" className="data-[state=active]:bg-white" data-testid="tab-all">
                          All
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="pending">
                        <EnrollmentList
                          enrollments={enrollments.filter(e => e.status === "submitted" || e.status === "under_review")}
                          loading={enrollmentsLoading}
                          onView={viewEnrollmentDetail}
                          emptyMessage="No pending enrollment requests."
                          emptyIcon={CheckCircle}
                        />
                      </TabsContent>
                      <TabsContent value="roster">
                        <EnrollmentList
                          enrollments={enrollments.filter(e => e.status === "approved")}
                          loading={enrollmentsLoading}
                          onView={viewEnrollmentDetail}
                          emptyMessage="No approved students yet."
                          emptyIcon={Users}
                        />
                      </TabsContent>
                      <TabsContent value="waitlist">
                        <EnrollmentList
                          enrollments={enrollments.filter(e => e.status === "waitlisted")}
                          loading={enrollmentsLoading}
                          onView={viewEnrollmentDetail}
                          emptyMessage="No waitlisted students."
                          emptyIcon={Clock}
                        />
                      </TabsContent>
                      <TabsContent value="all">
                        <EnrollmentList
                          enrollments={enrollments}
                          loading={enrollmentsLoading}
                          onView={viewEnrollmentDetail}
                          emptyMessage="No enrollments for this cohort."
                          emptyIcon={Users}
                        />
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Instructor Library — visible to instructors+admins for SOPs, guides, training materials */}
        <Card className="card-light mt-6">
          <CardContent className="pt-6">
            <InstructorLibraryPanel />
          </CardContent>
        </Card>
      </div>

      <StudentDetailModal
        enrollment={selectedEnrollment}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedEnrollment(null); }}
        onAction={handleReviewAction}
        allPrograms={allPrograms}
      />
    </LightPageLayout>
  );
};

const EnrollmentList = ({ enrollments, loading, onView, emptyMessage, emptyIcon: EmptyIcon }) => {
  if (loading) return <div className="flex justify-center py-8"><div className="spinner" /></div>;
  if (enrollments.length === 0) return (
    <div className="text-center py-8">
      <EmptyIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-400">{emptyMessage}</p>
    </div>
  );
  return (
    <div className="space-y-2">
      {enrollments.map((e) => (
        <EnrollmentRow key={e.enrollment_id} enrollment={e} onView={onView} />
      ))}
    </div>
  );
};

export default InstructorDashboard;
